// src/components/ceremony/SponsorStrip.js
// ============================================================================
// The reserved sponsor real estate on the pre/post ceremony surfaces.
//
// Founder: "The pre and post are also ways for me to test avenues of
// advertisement and sponsorships. There's tons of real estate on the screens."
//
// This does NOT invent a schema. CritCash already has a full advertiser system
// (functions/modules/advertisers.js) whose placement vocabulary already
// includes EVENT_BRANDING ("Logo/banner on tournament pages") and STREAM_OVERLAY
// ("Overlay during live matches") — both defined, seeded, and consumed by no
// screen until now. So a slot here is a real, sellable placement on day one:
// point an advertiser's campaign at `event_branding` and it appears.
//
// DEMO ADS ARE SUPPRESSED. adService falls back to canned Razer/G FUEL/Secretlab
// cards when nobody has bought the placement. That's fine in a lobby list; on a
// broadcast ceremony it would read as "CritCash has sponsors" when it doesn't.
// The slot renders empty instead — visible only in dev, so the real estate can
// be seen and sold without ever claiming a sponsor we don't have.
// ============================================================================

import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet, Platform } from 'react-native';

import { useAds } from '../../services/adService';
import { CEREMONY_COLORS as C } from '../../constants/ceremony';
import { anim } from './CeremonyCanvas';

const IS_DEV_HOST = () =>
  (typeof __DEV__ !== 'undefined' && __DEV__)
  || (Platform.OS === 'web'
    && typeof window !== 'undefined'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname));

function resolveSponsorLogoUri(s) {
  if (!s) return null;
  if (typeof s.logoUrl === 'string' && s.logoUrl.trim()) return s.logoUrl.trim();
  if (typeof s.logoAssetId === 'string' && /^[a-f0-9]{32}$/.test(s.logoAssetId)) {
    return `/sponsor-logo/${s.logoAssetId}`;
  }
  return null;
}

/**
 * @param {string} placement  advertisers.js PLACEMENT_TYPES value
 * @param {number} bottom     design-pixel offset from the canvas floor
 * @param {object|null} branding  event branding from getEventCeremony when branded === true
 */
export default function SponsorStrip({
  placement = 'event_branding',
  gameId = null,
  bottom = 34,
  label = 'PRESENTED BY',
  branding = null,
}) {
  const { ads } = useAds(placement, gameId);

  // Real campaigns only — never the canned fallbacks.
  const ad = useMemo(() => (ads || []).find((a) => a && !a.isDemo) || null, [ads]);

  const presenting = useMemo(() => {
    if (!branding || !Array.isArray(branding.sponsors)) return null;
    return branding.sponsors.find((s) => s && s.level === 'presenting') || null;
  }, [branding]);

  const featured = useMemo(() => {
    if (!branding || !Array.isArray(branding.sponsors)) return [];
    return branding.sponsors.filter((s) => s && s.level === 'featured');
  }, [branding]);

  if (presenting) {
    const logoUri = resolveSponsorLogoUri(presenting);
    return (
      <View
        style={[styles.wrap, { bottom }, anim('fadeUp', { delay: 900, duration: 700 })]}
        pointerEvents="none"
      >
        <Text style={styles.label}>PRESENTED BY</Text>
        <View style={styles.card}>
          {logoUri ? (
            <Image source={{ uri: logoUri }} style={styles.logo} resizeMode="contain" />
          ) : null}
          <Text style={styles.name} numberOfLines={1}>
            {presenting.name}
          </Text>
        </View>
      </View>
    );
  }

  if (featured.length > 0) {
    return (
      <View
        style={[styles.wrap, { bottom }, anim('fadeUp', { delay: 900, duration: 700 })]}
        pointerEvents="none"
      >
        <Text style={styles.label}>FEATURED SPONSORS</Text>
        <View style={styles.card}>
          {featured.map((f, idx) => {
            const logoUri = resolveSponsorLogoUri(f);
            return (
              <View key={f.sponsorshipId || f.name || `feat_${idx}`} style={styles.featuredItem}>
                {logoUri ? (
                  <Image source={{ uri: logoUri }} style={styles.logo} resizeMode="contain" />
                ) : null}
                <Text style={styles.name} numberOfLines={1}>
                  {f.name}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  }

  if (!ad) {
    if (!IS_DEV_HOST()) return null;
    return (
      <View style={[styles.wrap, { bottom }]} pointerEvents="none">
        <View style={styles.placeholder}>
          <Text style={styles.placeholderText}>
            {`SPONSOR SLOT  ·  ${placement}  ·  unsold`}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[styles.wrap, { bottom }, anim('fadeUp', { delay: 900, duration: 700 })]}
      pointerEvents="none"
    >
      <Text style={styles.label}>{label}</Text>
      <View style={styles.card}>
        {ad.imageUrl ? (
          <Image source={{ uri: ad.imageUrl }} style={styles.logo} resizeMode="contain" />
        ) : null}
        <Text style={[styles.name, ad.color ? { color: ad.color } : null]} numberOfLines={1}>
          {ad.name}
        </Text>
        {!!ad.tagline && (
          <Text style={styles.tagline} numberOfLines={1}>{ad.tagline}</Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  label: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 6,
    color: '#6a6a6a',
    marginBottom: 8,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: 'rgba(17,17,17,0.86)',
    borderWidth: 1,
    borderColor: C.BORDER,
  },
  logo: { width: 40, height: 40 },
  featuredItem: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  name: { fontSize: 24, fontWeight: '900', letterSpacing: 1, color: C.TEXT },
  tagline: { fontSize: 16, fontWeight: '700', color: C.DIM },
  placeholder: {
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#2e2e2e',
  },
  placeholderText: {
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 3,
    color: '#4a4a4a',
  },
});
